import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../constants/actionTypes';
import { actions as locationDashboardActions } from '../../actions/locationDashboardActions';
import { LOCATION_DASHBOARD_URL_PARAMS as URLParams } from '../../constants/constants';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import Spreadsheet from '../Common/Spreadsheet/Spreadsheet';
import './LocationFinancialsSpreadsheet.scss';
import Button from '../Common/Button';
import Notification from '../Common/Notification';
import Routes from '../../constants/routes';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import ToggleV6 from '../Common/ToggleV6/ToggleV6';

const VAT_SOURCE_PROP_NAMES = {
    financialsBudgetVat: "financialsBudget",
    financialsActualVat: "financialsActual",
    financialsDifferenceVat: "financialsDifference"
};
const VAT_PROP_NAMES = Object.keys(VAT_SOURCE_PROP_NAMES);
const MONEY_PROP_NAMES = ["financialsBudget", "financialsActual", "financialsUnitCost", "financialsDifference", ...VAT_PROP_NAMES];
const DIFFERENCE_PROP_NAMES = ["financialsDifference", "financialsDifferenceVat"];
const DIFFERENCE_PROP_NAME = "financialsDifference";
const DIFFERENCE_SOURCE_PROP_NAMES = ["financialsBudget", "financialsActual"];
const CALCULATED_PROP_NAMES = [...VAT_PROP_NAMES, DIFFERENCE_PROP_NAME];
const CURRENCY_PROP_NAMES = ["financialsBudget", "financialsActual", ...VAT_PROP_NAMES];
const TEXT_PROP_NAMES = ["financialsUnitName", "financialsVendor"];
const VAT_RATE_RENDER_DELAY_MS = 300;
const CONTAINER_HEIGHT_OFFSET_PX = 125;

// Used to skip the :has() JS fallback on browsers that support it natively.
const supportsHas = typeof CSS !== 'undefined' && CSS.supports('selector(:has(*))');

const CELL_REF_PATTERN = /^([A-Z]+)(\d+)$/;
const parseCellRef = (cell) => {
    const match = typeof cell === "string" ? CELL_REF_PATTERN.exec(cell) : null;
    return match ? { column: match[1], row: Number(match[2]) } : null;
};
const getCellColumn = (cell) => {
    const ref = parseCellRef(cell);
    return ref ? ref.column : "";
};
/** The grid numbers its columns from 1, in the same order as the spreadsheet letters: A -> "1". */
const getColumnId = (letter) => {
    let index = 0;
    for (let i = 0; i < letter.length; i++) {
        index = index * 26 + (letter.charCodeAt(i) - 64);
    }
    return index.toString();
};

class LocationFinancialsSpreadsheet extends React.Component {
    constructor(props, context) {
        super(props, context)
        this.spreadsheet = React.createRef();
        this.spreadsheetContainer = React.createRef();
        this.frozenHeaderRow = React.createRef();
        this.gridBodyEl = null;
        this.calculatedCellCss = {};
        this.formulaCells = new Set();
        this.hiddenColumnWidths = {};
        this.state = {
            locationTasks: [],
            rowValues: [],
            allowTaskBudgetEntry: false,
            isSubscriptionFinancialsActive: false,
            isLoading: false,
            spreadsheetHeader: [],
            showVat: false,
            vatPercentage: "",
            savedVatSetting: { showVat: false, vatPercentage: "" },
            frozenHeaderColumns: null,
            frozenHeaderTop: 0,
            frozenHeaderHeight: 0,
            frozenHeaderLeft: 0
        }
    }

    /** Null when the feature is disabled or no rows have loaded, since the grid isn't rendered then. */
    getSpreadsheet = () => this.spreadsheet.current && this.spreadsheet.current.spreadsheet;

    /**
     * The grid the spreadsheet draws itself with. Not part of the widget's public API, but it owns
     * the column layout - widths and hidden state - that both the cell editor and the frozen header
     * row have to be kept in step with.
     */
    getGrid = () => {
        const spreadsheet = this.getSpreadsheet();
        return spreadsheet ? spreadsheet._grid : null;
    };

    componentDidMount = async () => {
        this._isMounted = true;
        await this.loadData();
        window.addEventListener('resize', this.measureFrozenHeaderColumns);
    }

    componentWillUnmount = () => {
        this._isMounted = false;
        if (this.frozenHeaderRafId) {
            window.cancelAnimationFrame(this.frozenHeaderRafId);
        }
        if (this.vatRateRenderTimeoutId) {
            window.clearTimeout(this.vatRateRenderTimeoutId);
        }
        window.removeEventListener('resize', this.measureFrozenHeaderColumns);
        if (this.gridBodyEl) {
            this.gridBodyEl.removeEventListener('scroll', this.onGridScroll);
        }
        this.detachSpreadsheetEvents();
    }

    /**
     * The widget's own destructor leaves its event bus alone - unlike the grid's, which clears it -
     * so handlers attached to it outlive it. Nothing dispatches to them today, since the widget is
     * created and destroyed by the child component that renders it and goes out of scope with this
     * one, but detaching what we attached keeps that true if the widget is ever kept alive across
     * mounts.
     */
    detachSpreadsheetEvents = () => {
        if (!this.subscribedSpreadsheet) {
            return;
        }
        if (this.valueChangeEventId) {
            this.subscribedSpreadsheet.events.detach(this.valueChangeEventId);
            this.valueChangeEventId = null;
        }
        if (this.gridRedrawEventId) {
            this.subscribedSpreadsheet.events.detach(this.gridRedrawEventId);
            this.gridRedrawEventId = null;
        }
        this.subscribedSpreadsheet = null;
    }

    /**
     * Wrapper for retrieving the selected location from Redux.  We're now
     * storing it there instead of in local component state, because it has to
     * share that selected location state with the Task List.
     * 
     * This function also encapsulates the logic to override the normal operation
     * of sharing the redux selected location, by instead selecting the location
     * passed to this page via it's URL parameters.
     */
    getSelectedLocationKey = () => {
        if (this.props.urlParams && this.props.urlParams.locationKey) {
            return this.props.urlParams.locationKey.toString();
        } else {
            const reduxKey = (this.props.taskListState
                && this.props.taskListState.searchParams
                && this.props.taskListState.searchParams.locationKeys
                && this.props.taskListState.searchParams.locationKeys.length > 0) ? this.props.taskListState.searchParams.locationKeys[0].toString() : '';

            return reduxKey;
        }
    }

    /**
     * @param preserveVatSetting keeps the VAT toggle/rate the user is looking at instead of
     * replacing it with the stored one, for the case where saving that setting has just failed.
     */
    loadData = async (preserveVatSetting = false) => {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.locationDashboardActions.loadFinancialsSpreadsheet(this.getSelectedLocationKey());
            if (!this._isMounted) {
                return;
            }
            if (response.type === types.LOAD_LOCATION_FINANCIALS_SPREADSHEET_SUCCESS) {
                const savedVatPercentage = response.payload.vatPercentage === null || response.payload.vatPercentage === undefined
                    ? ""
                    : response.payload.vatPercentage.toString();
                const savedShowVat = !!response.payload.showVat;

                this.setState({
                    locationTasks: response.payload.listTasks,
                    rowValues: response.payload.listTasks.map(task => ({ ...task.rowData })),
                    allowTaskBudgetEntry: response.payload.allowTaskBudgetEntry,
                    isSubscriptionFinancialsActive: response.payload.isSubscriptionFinancialsActive,
                    spreadsheetHeader: response.payload.listTasksHeader,
                    showVat: preserveVatSetting ? this.state.showVat : savedShowVat,
                    vatPercentage: preserveVatSetting ? this.state.vatPercentage : savedVatPercentage,
                    savedVatSetting: { showVat: savedShowVat, vatPercentage: savedVatPercentage }
                }, () => {
                    // Not rendered (feature disabled / no rows) when the ref is still null.
                    const spreadsheet = this.getSpreadsheet();
                    if (!spreadsheet) {
                        return;
                    }
                    spreadsheet.contextMenu.data.remove(["lock", "clear", "columns", "rows"]);
                    this.loadDataTable();
                    this.subscribedSpreadsheet = spreadsheet;
                    if (this.valueChangeEventId) {
                        spreadsheet.events.detach(this.valueChangeEventId);
                    }
                    // afterValueChange, not afterEditEnd: the calculated columns also have to follow
                    // pasting, clearing with Delete and undo/redo, none of which end a cell edit.
                    this.valueChangeEventId = spreadsheet.events.on("afterValueChange", this.onCellValueChange, this);
                    if (this.gridRedrawEventId) {
                        spreadsheet.events.detach(this.gridRedrawEventId);
                    }
                    this.gridRedrawEventId = spreadsheet.events.on("gridRedraw", this.onGridRedraw, this);
                    this.frozenHeaderRafId = window.requestAnimationFrame(() => {
                        this.measureFrozenHeaderColumnsWithRetry();
                        if (!supportsHas) this.applyHasFallbackClasses();
                    });
                });
            } else if (response.type === types.LOAD_LOCATION_FINANCIALS_SPREADSHEET_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error.message);
        } finally {
            if (this._isMounted) {
                this.setState({ isLoading: false });
            }
        }
    }

    getHeaderDisplayLabel = (header) => {
        return CURRENCY_PROP_NAMES.includes(header.propName) ? `${header.value} (US$)` : header.value;
    };

    /**
     * Runs on every value the user puts in a cell, however they put it there (typing, pasting,
     * clearing, undo/redo), and feeds it back into the calculated columns of that row.
     * Ignores the writes this component makes itself, which would otherwise loop.
     */
    onCellValueChange = (cell) => {
        if (this.isWritingCells) {
            return;
        }

        if (this.getSpreadsheet().getFormula(cell)) {
            this.formulaCells.add(cell);
        } else {
            this.formulaCells.delete(cell);
        }

        this.applyCellEdit(cell);
        // A formula that reads this cell ("=C14*E14") recalculates silently, with no value change
        // event of its own, so every formula cell is re-read after each edit.
        this.formulaCells.forEach(formulaCell => {
            if (formulaCell !== cell) {
                this.applyCellEdit(formulaCell);
            }
        });
    };

    /** A cell's current value, blank rather than null/undefined when it holds nothing. */
    readCellValue = (cell) => {
        const value = this.getSpreadsheet().getValue(cell);
        return value === null || value === undefined ? "" : value;
    };

    /**
     * Feeds one cell into state and the calculated columns of its row. Takes the value back off
     * the grid instead of the one that was typed, so a formula cell contributes its result
     * ("=6*6" -> 36) rather than its text.
     */
    applyCellEdit = (cell) => {
        const colLetter = getCellColumn(cell);
        const colHeader = this.state.spreadsheetHeader.find(h => getCellColumn(h.cell) === colLetter);

        if (!colHeader || CALCULATED_PROP_NAMES.includes(colHeader.propName)) {
            return;
        }

        const value = this.readCellValue(cell);
        if (TEXT_PROP_NAMES.includes(colHeader.propName)) {
            this.rememberEditedValue(cell, colHeader, value);
            return;
        }
        const parsedValue = Number(value);
        // Blank stays blank rather than becoming a literal 0, so a cleared Budget/Actual cell
        // isn't counted as "0 spent/budgeted" by the Difference calculation.
        const valueToNum = value === "" ? "" : (isNaN(parsedValue) ? "" : parsedValue);
        const roundedValue = valueToNum === "" ? "" : Math.round(valueToNum * 100) / 100;

        this.isWritingCells = true;
        try {
            const spreadsheet = this.getSpreadsheet();
            // Writing the rounded value back into a formula cell would replace the formula with
            // that number, and the user still needs it to follow the cells it reads.
            if (valueToNum !== roundedValue && !this.formulaCells.has(cell)) {
                spreadsheet.setValue(cell, roundedValue);
            }
            if (MONEY_PROP_NAMES.includes(colHeader.propName) && !DIFFERENCE_PROP_NAMES.includes(colHeader.propName)) {
                spreadsheet.setFormat(cell, "#,##0.00");
            }
        } finally {
            this.isWritingCells = false;
        }

        this.rememberEditedValue(cell, colHeader, roundedValue);
    };

    /**
     * What one cell has to be sent as. Read off the grid rather than out of serialize(), which
     * gives a formula cell its "=C14*E14" text: the API takes numbers, and only the result is
     * stored. Returns undefined for anything not worth sending, leaving that field out.
     */
    getSaveValue = (cell, propName) => {
        const value = this.readCellValue(cell);
        if (value === "") {
            return undefined;
        }
        if (TEXT_PROP_NAMES.includes(propName)) {
            return value;
        }

        const numValue = Number(value);
        if (isNaN(numValue)) {
            return undefined;
        }
        return MONEY_PROP_NAMES.includes(propName) ? Math.round(numValue * 100) / 100 : numValue;
    };

    /**
     * Mirrors an edited cell into this.state.rowValues, so that re-rendering the grid
     * (VAT toggled, VAT rate changed) keeps the user's unsaved edits, and so the calculated
     * columns of the edited row can be recalculated from the new value.
     */
    rememberEditedValue = (cell, colHeader, value) => {
        if (!colHeader || !this._isMounted) {
            return;
        }

        const ref = parseCellRef(cell);
        const rowIndex = ref ? ref.row - 2 : -1;
        if (!this.state.rowValues[rowIndex]) {
            return;
        }

        const newValue = value === "" || value === null || value === undefined ? "" : value.toString();
        this.setState(prevState => ({
            rowValues: prevState.rowValues.map((row, index) =>
                index === rowIndex ? this.applyEditToRow(row, colHeader.propName, newValue) : row)
        }), () => this.refreshCalculatedCellsForRow(rowIndex));
    };

    /** Applies one edited value to a row, along with anything calculated from it. */
    applyEditToRow = (row, propName, newValue) => {
        const editedRow = { ...row, [propName]: newValue };
        if (!DIFFERENCE_SOURCE_PROP_NAMES.includes(propName)) {
            return editedRow;
        }
        return { ...editedRow, [DIFFERENCE_PROP_NAME]: this.getDifferenceValue(editedRow) };
    };

    /** Difference = Budget - Actual, with a missing side counted as nothing spent/budgeted. */
    getDifferenceValue = (row) => {
        const isBlank = (value) => value === "" || value === null || value === undefined || isNaN(Number(value));
        const budget = row[DIFFERENCE_SOURCE_PROP_NAMES[0]];
        const actual = row[DIFFERENCE_SOURCE_PROP_NAMES[1]];

        if (isBlank(budget) && isBlank(actual)) {
            return "";
        }
        const difference = (isBlank(budget) ? 0 : Number(budget)) - (isBlank(actual) ? 0 : Number(actual));
        return (Math.round(difference * 100) / 100).toString();
    };

    getVatMultiplier = () => {
        const percentage = Number(this.state.vatPercentage);
        return isNaN(percentage) ? 1 : 1 + (percentage / 100);
    };

    /** Budget/Actual/Difference + VAT = source value * (1 + VAT rate), to 2 decimals. */
    getVatValue = (sourceValue) => {
        if (sourceValue === "" || sourceValue === null || sourceValue === undefined || isNaN(Number(sourceValue))) {
            return "";
        }
        return Math.round(Number(sourceValue) * this.getVatMultiplier() * 100) / 100;
    };

    /**
     * Difference columns show negatives as "(1,234.56)" in red instead of "-1,234.56",
     * which the grid's number formatter can't do, so they're rendered as plain text.
     */
    getDifferenceDisplay = (rawValue) => {
        if (rawValue === "" || rawValue === null || rawValue === undefined || isNaN(Number(rawValue))) {
            return { value: rawValue ?? "", isNegative: false };
        }

        const numVal = Number(rawValue);
        const formatted = Math.abs(numVal).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return { value: numVal < 0 ? `(${formatted})` : formatted, isNegative: numVal < 0 };
    };

    refreshCalculatedCellsForRow = (rowIndex) => {
        const spreadsheet = this.getSpreadsheet();
        const row = this.state.rowValues[rowIndex];
        if (!spreadsheet || !row) {
            return;
        }

        this.isWritingCells = true;
        try {
            this.writeCalculatedCellsForRow(spreadsheet, row, rowIndex, this.getColumnMeta());
        } finally {
            this.isWritingCells = false;
        }
    };

    writeCalculatedCellsForRow = (spreadsheet, row, rowIndex, columnMeta) => {
        for (let header of this.state.spreadsheetHeader) {
            if (!CALCULATED_PROP_NAMES.includes(header.propName)) {
                continue;
            }

            const colLetter = getCellColumn(header.cell);
            const isVat = VAT_PROP_NAMES.includes(header.propName);
            const value = isVat
                ? this.getVatValue(row[VAT_SOURCE_PROP_NAMES[header.propName]])
                : (row[header.propName] ?? "");

            this.writeCalculatedCell(spreadsheet, colLetter + (rowIndex + 2).toString(), columnMeta[colLetter], value, isVat);
        }
    };

    /**
     * Writes one calculated cell, class list included: a Difference that changes sign has to
     * gain or lose the red "negative-diff" class, and setStyle replaces the whole class list,
     * so the shading and the show/hide of the VAT columns have to be re-sent along with it.
     */
    writeCalculatedCell = (spreadsheet, cell, meta, value, isVat) => {
        if (!meta) {
            return;
        }

        const display = meta.isDifference ? this.getDifferenceDisplay(value) : { value: value, isNegative: false };
        const css = this.buildCellCss(meta, display.isNegative ? "negative-diff" : "");
        const wasLocked = spreadsheet.isLocked(cell);

        // Only the "(1,234.56)" negatives need the text format: setValue turns anything that parses
        // as a number back into one whatever the format says, which would drop the trailing zeros
        // of a positive Difference ("440.00" -> 440). Positives keep the number format instead,
        // which prints the two decimals itself.
        spreadsheet.unlock(cell);
        spreadsheet.setFormat(cell, display.isNegative ? "@" : "#,##0.00");
        spreadsheet.setValue(cell, display.isNegative ? display.value : value);
        if (this.calculatedCellCss[cell] !== css) {
            spreadsheet.setStyle(cell, css || " ");
            this.calculatedCellCss[cell] = css;
        }
        // VAT columns are always calculated and locked; others keep whatever lock state they had.
        if (isVat || wasLocked) {
            spreadsheet.lock(cell);
        }
    };

    refreshAllCalculatedCells = () => {
        const spreadsheet = this.getSpreadsheet();
        const columnMeta = this.getColumnMeta();
        if (!spreadsheet) return;

        this.isWritingCells = true;
        try {
            this.state.rowValues.forEach((row, rowIndex) => this.writeCalculatedCellsForRow(spreadsheet, row, rowIndex, columnMeta));
        } finally {
            this.isWritingCells = false;
        }
    };

    /** How each column has to be rendered, keyed by column letter and off the header's property name. */
    getColumnMeta = () => {
        const columnMeta = {};
        for (let header of this.state.spreadsheetHeader) {
            columnMeta[getCellColumn(header.cell)] = {
                isDifference: DIFFERENCE_PROP_NAMES.includes(header.propName),
                isMoney: MONEY_PROP_NAMES.includes(header.propName),
                isVat: VAT_PROP_NAMES.includes(header.propName)
            };
        }
        return columnMeta;
    };

    buildCellCss = (meta, extraCss) => {
        const classes = [];
        if (meta.isVat) classes.push("vat-col");
        if (extraCss) classes.push(extraCss);
        return classes.join(" ");
    };

    /** The Id column, and the VAT columns while "Show VAT" is off. */
    isHeaderHidden = (header) => header.css === "hiddenColumn"
        || (VAT_PROP_NAMES.includes(header.propName) && !this.state.showVat);

    /**
     * Hides columns through the grid rather than with CSS, and zeroes their width while they are
     * hidden. A column that is only hidden in CSS is still part of the grid's own column layout,
     * so it keeps contributing its width to the position the grid computes for the cell editor.
     *
     * Marking it hidden is not enough on its own either. The grid's own geometry skips hidden
     * columns, but the selection overlay - the box around the focused cell, and the outlines of a
     * dragged or copied range - adds up the width of every column before the selected one without
     * looking at `hidden`, so each hidden column still to its left pushed the box a column
     * further right: with the three VAT columns off, clicking column L drew the box over column O.
     * Zeroing the width leaves nothing for that sum to pick up, and both agree again.
     *
     * The grid's own hideColumn/showColumn are not used because they would paint once with the
     * old width still in place; the widths and the total are set here and painted once instead.
     */
    syncHiddenColumns = () => {
        const grid = this.getGrid();
        if (!grid) {
            return;
        }

        let hasChanged = false;
        for (let header of this.state.spreadsheetHeader) {
            const columnId = getColumnId(getCellColumn(header.cell));
            const column = grid.getColumn(columnId);
            if (!column) {
                continue;
            }

            if (this.isHeaderHidden(header)) {
                if (column.$width !== 0) {
                    this.hiddenColumnWidths[columnId] = column.$width;
                    column.$width = 0;
                    hasChanged = true;
                }
                if (!column.hidden) {
                    column.hidden = true;
                    hasChanged = true;
                }
            } else if (column.hidden) {
                column.hidden = false;
                column.$width = this.hiddenColumnWidths[columnId] || column.width;
                hasChanged = true;
            }
        }

        if (hasChanged) {
            // Hidden columns are 0 wide, so every column can be counted without filtering.
            grid.config.$totalWidth = grid.config.columns.reduce((total, column) => total + column.$width, 0);
            grid.paint();
        }
    };

    loadDataTable = () => {
        const spreadsheet = this.getSpreadsheet();
        this.calculatedCellCss = {};
        this.formulaCells.clear();

        for (let item of this.state.spreadsheetHeader) {
            if (item.isLocked) {
                spreadsheet.lock(item.cell + ":" + getCellColumn(item.cell) + (this.state.locationTasks.length + 1).toString());
            }
        }

        const columnMeta = this.getColumnMeta();

        const headerRow = this.state.spreadsheetHeader.map(header => {
            const meta = columnMeta[getCellColumn(header.cell)];
            return {
                cell: header.cell,
                value: this.getHeaderDisplayLabel(header),
                isLocked: header.isLocked,
                format: meta.isMoney ? "#,##0.00" : header.format,
                css: this.buildCellCss(meta),
                propName: header.propName
            };
        });

        const dataRows = this.state.rowValues.map((row, index) => {
            const rowIndex = index + 2;
            return this.state.spreadsheetHeader.map(header => {
                const colLetter = getCellColumn(header.cell);
                const meta = columnMeta[colLetter];
                const rawValue = meta.isVat
                    ? this.getVatValue(row[VAT_SOURCE_PROP_NAMES[header.propName]])
                    : (row[header.propName] ?? "");
                let value = rawValue;
                let format = header.format ?? "";
                let extraCss = "";

                if (meta.isDifference) {
                    const display = this.getDifferenceDisplay(rawValue);
                    value = display.value;
                    if (display.isNegative) {
                        extraCss = "negative-diff";
                    }
                    format = "@";
                } else if (meta.isMoney) {
                    format = "#,##0.00";
                }

                const cell = colLetter + rowIndex;
                const css = this.buildCellCss(meta, extraCss);
                this.calculatedCellCss[cell] = css;
                return { cell, value, format, css };
            });
        }).flat();

        const dataTable = [...headerRow, ...dataRows];

        this.isWritingCells = true;
        try {
            spreadsheet.parse(dataTable);
        } finally {
            this.isWritingCells = false;
        }
        this.syncHiddenColumns();
        this.refreshAllCalculatedCells();
    };

    // Adds .is-vat-col to .dhx_grid-cell wrappers so that the CSS fallback rule
    // (for browsers without :has() support) can shade the VAT columns.
    applyHasFallbackClasses = () => {
        const container = this.spreadsheetContainer.current;
        if (!container) return;
        container.querySelectorAll('.dhx_grid-cell').forEach(cell => {
            cell.classList.toggle('is-vat-col', !!cell.querySelector('.vat-col'));
        });
    };

    /**
     * Binds the scroll listener to the grid's scrolling body, and rebinds it if the grid ever
     * replaces that element. Called from the measurement rather than once after loading: the grid
     * has not always painted by then, and a single early attempt that found no body left the
     * header row deaf to horizontal scrolling for the rest of the session - it stayed where the
     * last redraw had put it while the columns slid out from under it.
     */
    attachGridScrollListener = (gridBody) => {
        if (this.gridBodyEl === gridBody) {
            return;
        }
        if (this.gridBodyEl) {
            this.gridBodyEl.removeEventListener("scroll", this.onGridScroll);
        }
        this.gridBodyEl = gridBody;
        this.gridBodyEl.addEventListener("scroll", this.onGridScroll);
    };

    measureFrozenHeaderColumnsWithRetry = (attempt = 0) => {
        const measured = this.measureFrozenHeaderColumns();
        if (!measured && attempt < 30) {
            this.frozenHeaderRafId = window.requestAnimationFrame(() => this.measureFrozenHeaderColumnsWithRetry(attempt + 1));
        }
    };

    /** The header the API sent for a given grid column id, so a rendered cell can find its label. */
    getHeadersByColumnId = () => {
        const headersByColumnId = {};
        for (let header of this.state.spreadsheetHeader) {
            headersByColumnId[getColumnId(getCellColumn(header.cell))] = header;
        }
        return headersByColumnId;
    };

    /**
     * Rebuilds the frozen header row: one cell per visible column, in order, each as wide as the
     * grid has made that column. The widths come from the grid's column layout, which lines the
     * strip up with the columns exactly - the row as a whole is then slid sideways to follow the
     * grid's horizontal scrolling, which is what keeps a cell over its column.
     *
     * The strip covers every visible column rather than only the ones on screen, because the grid
     * renders just the columns in view and the cells either side of them still have to be there
     * for the row to line up once they scroll in.
     *
     * The sticky column-letter row is what the strip hangs off vertically: it stays put while the
     * body scrolls, and the data always begins directly below it. A data row is no good for that -
     * the grid keeps a few rendered above the viewport, so the first one sits at a different
     * offset depending on how far down the grid has been scrolled.
     */
    measureFrozenHeaderColumns = () => {
        const container = this.spreadsheetContainer.current;
        const grid = this.getGrid();
        if (!container || !grid || !grid.config || !grid.config.columns) {
            return false;
        }

        const gridBody = container.querySelector(".dhx_grid-body");
        const letterRow = gridBody && gridBody.querySelector(".dhx_header-wrapper");
        const firstRow = container.querySelector(".dhx_grid-row");
        if (!gridBody || !letterRow || !firstRow) {
            return false;
        }

        this.attachGridScrollListener(gridBody);

        const containerRect = container.getBoundingClientRect();
        const bodyRect = gridBody.getBoundingClientRect();
        const letterRowRect = letterRow.getBoundingClientRect();
        const rowHeight = firstRow.getBoundingClientRect().height || grid.config.rowHeight;
        if (bodyRect.width === 0 || bodyRect.height === 0 || !rowHeight) {
            return false;
        }

        // The row-number gutter is painted over the left of the grid and doesn't scroll, so the
        // strip starts to the right of it and clips whatever scrolls underneath.
        const [gutterColumn, ...dataColumns] = grid.config.columns;
        const clipLeft = (bodyRect.left - containerRect.left) + (gutterColumn ? gutterColumn.$width : 0);

        const headersByColumnId = this.getHeadersByColumnId();
        const columns = dataColumns.filter(column => !column.hidden).map(column => {
            const header = headersByColumnId[column.id];
            return {
                width: column.$width,
                text: header ? this.getHeaderDisplayLabel(header) : "",
                isVat: !!(header && VAT_PROP_NAMES.includes(header.propName))
            };
        });
        this.applyFrozenHeaderScroll();

        this.setFrozenHeaderState({
            frozenHeaderColumns: columns,
            frozenHeaderTop: letterRowRect.bottom - containerRect.top,
            frozenHeaderHeight: rowHeight,
            frozenHeaderLeft: clipLeft
        });
        return true;
    };

    /** Re-renders only on a real change: the grid redraws on plain scrolling as well as on resizing. */
    setFrozenHeaderState = (frozenHeader) => {
        if (!this._isMounted) {
            return;
        }

        const current = this.state.frozenHeaderColumns;
        const next = frozenHeader.frozenHeaderColumns;
        const isUnchanged = current
            && this.state.frozenHeaderTop === frozenHeader.frozenHeaderTop
            && this.state.frozenHeaderHeight === frozenHeader.frozenHeaderHeight
            && this.state.frozenHeaderLeft === frozenHeader.frozenHeaderLeft
            && current.length === next.length
            && current.every((column, index) => column.width === next[index].width
                && column.text === next[index].text
                && column.isVat === next[index].isVat);

        if (!isUnchanged) {
            this.setState(frozenHeader, this.applyFrozenHeaderScroll);
        }
    };

    /**
     * The grid redraws itself after a column resize - dragging a separator or double-clicking it to
     * auto-fit - without telling anyone the widths changed, so the header row is re-measured on
     * every redraw instead.
     */
    onGridRedraw = () => {
        if (this.frozenHeaderRafId) {
            window.cancelAnimationFrame(this.frozenHeaderRafId);
        }
        this.frozenHeaderRafId = window.requestAnimationFrame(() => {
            this.measureFrozenHeaderColumns();
            if (!supportsHas) this.applyHasFallbackClasses();
        });
    };

    /**
     * Slides the strip to match how far the grid has been scrolled sideways.
     *
     * Written straight to the node rather than held in state: going through a state update meant a
     * frame of latency at best, and scroll events arriving faster than the animation frame they
     * scheduled kept cancelling it, so the strip sat still during a scroll and only caught up once
     * the scrolling stopped.
     */
    applyFrozenHeaderScroll = () => {
        if (this.frozenHeaderRow.current && this.gridBodyEl) {
            this.frozenHeaderRow.current.style.transform = `translateX(${-this.gridBodyEl.scrollLeft}px)`;
        }
    };

    onGridScroll = () => {
        this.applyFrozenHeaderScroll();
    };

    onShowVatChange = (name, checked) => {
        this.setState({
            showVat: checked,
            vatPercentage: checked && this.state.vatPercentage === "" ? "0" : this.state.vatPercentage
        }, this.refreshVatColumns);
    };

    onVatPercentageChange = (e) => {
        const rawValue = e.target.value;
        if (rawValue !== "" && (!/^\d{0,3}(\.\d{0,2})?$/.test(rawValue) || Number(rawValue) > 100)) {
            return;
        }

        this.setState({ vatPercentage: rawValue }, () => {
            if (this.vatRateRenderTimeoutId) {
                window.clearTimeout(this.vatRateRenderTimeoutId);
            }
            this.vatRateRenderTimeoutId = window.setTimeout(this.refreshVatColumns, VAT_RATE_RENDER_DELAY_MS);
        });
    };

    refreshVatColumns = () => {
        if (!this.spreadsheet.current || !this.state.spreadsheetHeader.length) {
            return;
        }

        this.syncHiddenColumns();
        this.refreshAllCalculatedCells();
        this.frozenHeaderRafId = window.requestAnimationFrame(() => {
            this.measureFrozenHeaderColumnsWithRetry();
            if (!supportsHas) this.applyHasFallbackClasses();
        });
    };

    hasVatSettingChanged = () => {
        const normalizeRate = (rate) => (rate === "" || rate === null || rate === undefined || isNaN(Number(rate)) ? 0 : Number(rate));

        return this.state.showVat !== this.state.savedVatSetting.showVat
            || normalizeRate(this.state.vatPercentage) !== normalizeRate(this.state.savedVatSetting.vatPercentage);
    };

    /**
     * Persists the "Show VAT" toggle and rate against this user and this location only.
     * Runs on every Save, whether the toggle is on or off, so the stored preference always
     * matches what the user is looking at.
     */
    saveVatSetting = async () => {
        const vatPercentage = this.state.vatPercentage === "" ? null : Number(this.state.vatPercentage);

        try {
            const response = await this.props.locationDashboardActions.saveFinancialsVatSetting(
                this.getSelectedLocationKey(), this.state.showVat, vatPercentage);
            if (!this._isMounted) {
                return false;
            }

            if (response.type !== types.SAVE_LOCATION_FINANCIALS_VAT_SETTING_SUCCESS) {
                Notification("error", "Error", response.payload.message);
                return false;
            }

            this.setState({
                savedVatSetting: { showVat: this.state.showVat, vatPercentage: this.state.vatPercentage }
            });
            return true;
        } catch (error) {
            console.error("Saving the VAT setting failed:", error);
            Notification("error", "Error", error.message);
            return false;
        }
    };

    onSaveClick = async () => {
        this.setState({ isLoading: true });
        try {
            const spreadsheet = this.getSpreadsheet();
            if (!spreadsheet) {
                return;
            }
            spreadsheet.endEdit();

            const vatSettingChanged = this.hasVatSettingChanged();
            const vatSettingSaved = vatSettingChanged ? await this.saveVatSetting() : true;
            if (!this._isMounted) {
                return;
            }

            const sheetData = spreadsheet.serialize().sheets[0].data;

            let propColDict = {};
            for (let item of this.state.spreadsheetHeader) {
                if (item.isSentBack && !VAT_PROP_NAMES.includes(item.propName)) {
                    propColDict[getCellColumn(item.cell)] = item.propName;
                }
            }

            const groupedData = {};
            for (let { cell } of sheetData) {
                const ref = parseCellRef(cell);
                if (!ref) continue;

                if (!groupedData[ref.row]) groupedData[ref.row] = [];
                groupedData[ref.row].push({ cell });
            }

            delete groupedData["1"];

            const savedTaskDataFromSheet = Object.values(groupedData).map(rowCells => {
                let taskDetails = {};
                for (let { cell } of rowCells) {
                    const propName = propColDict[getCellColumn(cell)];
                    const value = propName ? this.getSaveValue(cell, propName) : undefined;
                    if (value !== undefined) {
                        taskDetails[propName] = value;
                    }
                }
                return taskDetails;
            });

            const shortTaskArrayFromState = this.state.locationTasks.map(task => {
                let taskDetails = {};
                for (let item of this.state.spreadsheetHeader) {
                    if (item.isSentBack && !VAT_PROP_NAMES.includes(item.propName)
                        && task.rowData[item.propName] !== undefined && task.rowData[item.propName] !== null) {
                        taskDetails[item.propName] = task.rowData[item.propName];
                    }
                }
                return taskDetails;
            });

            const taskEqual = (a, b) =>
                Object.keys(a).length === Object.keys(b).length &&
                Object.keys(a).every(key => a[key] === b[key]);

            const updatedTasks = savedTaskDataFromSheet.filter(newTask =>
                !shortTaskArrayFromState.some(origTask => taskEqual(origTask, newTask))
            );

            if (updatedTasks.length > 0) {
                try {
                    const response = await this.props.locationDashboardActions.saveFinancialsSpreadsheet(updatedTasks);
                    if (!this._isMounted) {
                        return;
                    }
                    if (response.type === types.SAVE_LOCATION_FINANCIALS_SPREADSHEET_SUCCESS) {
                        this.loadData(!vatSettingSaved);
                        if (vatSettingSaved) {
                            Notification("success", "Financial Data Updated", "");
                        } else {
                            Notification("warning", "Financial Data Updated", "The VAT setting could not be saved.");
                        }
                    } else {
                        Notification("error", "Error", response.payload.message);
                    }
                } catch (error) {
                    console.error("Save failed:", error);
                    Notification("error", "Error", error.message);
                }
            } else if (vatSettingChanged && vatSettingSaved) {
                Notification("success", "VAT Settings Updated", "");
            } else if (!vatSettingChanged) {
                Notification("info", "Nothing to Update", "");
            }
        } finally {
            if (this._isMounted) {
                this.setState({ isLoading: false });
            }
        }
    };


    onBackClick = () => {
        this.props.history.push(Routes.DashboardLocation);
    };

    render() {
        return (
            <LoadingIndicator isLoading={this.state.isLoading} isFullscreen={true} >
                <div
                    className="financials-spreadsheet-container"
                    style={{ height: (window.innerHeight - CONTAINER_HEIGHT_OFFSET_PX) + "px" }}
                    ref={this.spreadsheetContainer}>
                    {this.state.isSubscriptionFinancialsActive &&
                        this.state.spreadsheetHeader.length > 0 ?
                        <>
                            <div className="vat-toolbar">
                                <ToggleV6 name="showVat" text="Show VAT" checked={this.state.showVat} onChange={this.onShowVatChange} />
                                {this.state.showVat &&
                                    <div className="vat-rate">
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            className="vat-rate-input"
                                            aria-label="VAT percentage"
                                            value={this.state.vatPercentage}
                                            onChange={this.onVatPercentageChange} />
                                        <span className="vat-rate-suffix">%</span>
                                    </div>
                                }
                            </div>
                            <Spreadsheet
                                colsCount={this.state.spreadsheetHeader.length}
                                rowsCount={this.state.locationTasks.length + 1}
                                editLine={true}
                                menu={false}
                                multiSheets={false}
                                ref={this.spreadsheet} />
                            {this.state.frozenHeaderColumns &&
                                <div
                                    className="frozen-header-clip"
                                    style={{ top: this.state.frozenHeaderTop, height: this.state.frozenHeaderHeight, left: this.state.frozenHeaderLeft }}>
                                    <div className="frozen-header-row" ref={this.frozenHeaderRow}>
                                        {this.state.frozenHeaderColumns.map((col, index) =>
                                            <div
                                                key={index}
                                                className={col.isVat ? "frozen-header-cell vat-col" : "frozen-header-cell"}
                                                style={{ width: col.width, height: this.state.frozenHeaderHeight }}>
                                                {col.text}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            <div className="button-group">
                                <Button type="button" text="Save" bootstrapClass="primary" onClick={this.onSaveClick} />
                                <Button type="button" text="Back" bootstrapClass="secondary" onClick={this.onBackClick} />
                            </div>
                        </>
                        :
                        !this.state.isLoading &&
                        <div className="error-msg">This subscription does not have the task financials feature enabled.</div>
                    }
                </div>
            </LoadingIndicator>
        )
    }
}

LocationFinancialsSpreadsheet.propTypes = {
    locationDashboardActions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    taskListState: PropTypes.object.isRequired,
    user: PropTypes.object
};

function mapStateToProps(state, ownProps) {
    let urlParams = {
        locationKey: ""
    }
    const searchParams = new URLSearchParams(ownProps.location.search);
    if (searchParams.has(URLParams.locationKey)) {
        urlParams.locationKey = searchParams.get(URLParams.locationKey);
    }

    return {
        urlParams: urlParams,
        history: ownProps.history,
        taskListState: state.taskListState,
        user: state.user
    };
}

function mapDispatchToProps(dispatch) {
    return {
        locationDashboardActions: bindActionCreators(locationDashboardActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationFinancialsSpreadsheet);